export interface TextMeasurer {
  font: string;
  measureText(text: string): { width: number };
}

export interface FitTextOptions {
  maxWidth: number;
  /** The design size, px. */
  fontSize: number;
  /** Smallest size it may shrink to before cutting with «…». */
  minFontSize: number;
  /** The canvas `font` for a size: `(px) => \`700 ${px}px Unbounded\``. */
  font: (sizePx: number) => string;
  /** Lines it may wrap to by words; 1 — never wraps. */
  maxLines?: number;
}

export interface FittedText {
  lines: string[];
  fontSize: number;
  /** The canvas `font` the lines were measured with — set it before drawing them. */
  font: string;
}

const ELLIPSIS = '…';

/** Cuts the text to fit with «…» at the end; the widest prefix that fits, found by halving. */
function ellipsize(measurer: TextMeasurer, text: string, maxWidth: number): string {
  if (measurer.measureText(text).width <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const candidate = text.slice(0, mid).trimEnd() + ELLIPSIS;
    if (measurer.measureText(candidate).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo === 0 ? ELLIPSIS : text.slice(0, lo).trimEnd() + ELLIPSIS;
}

/** Greedy word wrap; words that do not fit in `maxLines` stay on the last line. */
function wrap(measurer: TextMeasurer, words: string[], maxWidth: number, maxLines: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (!line || measurer.measureText(candidate).width <= maxWidth || lines.length === maxLines - 1) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Fits a name into a box the way the video sets need it: the design size first, wrapped by words into `maxLines`;
 * too wide — the size goes down 1 px at a time to `minFontSize`; still too wide — the last line is cut with «…».
 * Never an overflow. Letter spacing is measured too: set `ctx.letterSpacing` before calling.
 *
 * It measures many times, so a scene fits its texts once when it is built, not on every frame.
 */
export function fitText(measurer: TextMeasurer, text: string, options: FitTextOptions): FittedText {
  const { maxWidth, fontSize, minFontSize, font, maxLines = 1 } = options;
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return { lines: [], fontSize, font: font(fontSize) };
  }

  for (let size = fontSize; size >= minFontSize; size -= 1) {
    measurer.font = font(size);
    const lines = maxLines === 1 ? [words.join(' ')] : wrap(measurer, words, maxWidth, maxLines);
    if (lines.every((line) => measurer.measureText(line).width <= maxWidth)) {
      return { lines, fontSize: size, font: measurer.font };
    }
  }

  const size = Math.min(fontSize, minFontSize);
  measurer.font = font(size);
  const lines = maxLines === 1 ? [words.join(' ')] : wrap(measurer, words, maxWidth, maxLines);
  return { lines: lines.map((line) => ellipsize(measurer, line, maxWidth)), fontSize: size, font: measurer.font };
}
